import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Award, BookOpen, Calendar, Download, Video } from 'lucide-react';
import { useUser } from '../contexts/AuthContext';
import { userAPI } from '../utils/api';

const typeLabels = {
  webinar: 'Webinar',
  mentorship: 'Mentorship',
  workshop: 'Workshop',
  guidance: '1:1 Guidance',
};

export default function DashboardPage() {
  const { user } = useUser();
  const [courses, setCourses] = useState([]);
  const [live, setLive] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [state, setState] = useState('loading');
  const [downloadError, setDownloadError] = useState('');

  const load = useCallback(async () => {
    try {
      setState('loading');
      const [courseResponse, liveResponse, certificateResponse] = await Promise.all([
        userAPI.getEnrolledCourses(),
        userAPI.getEnrolledLivePrograms(),
        userAPI.getCertificates(),
      ]);
      setCourses(courseResponse.data.data || []);
      setLive(liveResponse.data.data || []);
      setCertificates(certificateResponse.data.data || []);
      setState('ready');
    } catch {
      setState('error');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const visible = () => document.visibilityState === 'visible' && load();
    document.addEventListener('visibilitychange', visible);
    return () => document.removeEventListener('visibilitychange', visible);
  }, [load]);

  const downloadCertificate = async (certificate) => {
    try {
      setDownloadError('');
      const response = await userAPI.downloadCertificate(certificate.id);
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${certificate.number}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      setDownloadError('Certificate download failed. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-black pb-16 pt-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold">Welcome back, {user?.firstName || 'Student'}!</h1>
        <p className="mt-2 text-gray-400">Continue your learning journey</p>

        {state === 'loading' && <p role="status" className="py-16 text-gray-300">Loading your learning dashboard...</p>}
        {state === 'error' && (
          <div role="alert" className="mt-8 rounded border border-red-500 bg-red-950/40 p-4 text-red-200">
            Your dashboard could not be loaded. <button className="underline" onClick={load}>Try again</button>
          </div>
        )}

        {state === 'ready' && (
          <>
            <CourseSection courses={courses} />
            <LiveSection live={live} />
            <CertificateSection certificates={certificates} downloadError={downloadError} onDownload={downloadCertificate} />
          </>
        )}
      </div>
    </div>
  );
}

function CourseSection({ courses }) {
  return (
    <section className="mt-10">
      <h2 className="text-2xl font-bold">My Courses</h2>
      {courses.length ? (
        <div className="mt-5 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {courses.map((enrollment) => (
            <Link key={enrollment.id} to={`/courses/${enrollment.course.slug}`} className="rounded-xl border border-zinc-800 bg-zinc-900 p-6 hover:border-yellow-500">
              <h3 className="text-xl font-semibold">{enrollment.course.title}</h3>
              <div className="mt-5 flex justify-between text-sm">
                <span className="text-gray-400">Progress</span>
                <span className="text-yellow-400">{enrollment.progress_percent}%</span>
              </div>
              <div className="mt-2 h-2 rounded bg-zinc-800">
                <div className="h-2 rounded bg-yellow-400" style={{ width: `${Math.min(100, enrollment.progress_percent)}%` }} />
              </div>
            </Link>
          ))}
        </div>
      ) : <Empty icon={BookOpen} text="You haven't enrolled in any courses yet" href="/courses" link="Browse Courses" />}
    </section>
  );
}

function LiveSection({ live }) {
  return (
    <section className="mt-14">
      <h2 className="text-2xl font-bold">My Live Programs</h2>
      {live.length ? (
        <div className="mt-5 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {live.map((item) => (
            <article key={item.id} className="rounded-xl border border-zinc-800 bg-zinc-900 p-6">
              <span className="rounded bg-amber-500/10 px-3 py-1 text-xs text-amber-400">{typeLabels[item.program.type] || item.program.type}</span>
              <h3 className="mt-3 text-xl font-semibold">{item.program.title}</h3>
              <p className="mt-2 text-sm text-gray-400">
                Registration: <span className="text-amber-300">{item.status}</span>
                {item.waitlist_position ? ` - Position ${item.waitlist_position}` : ''}
              </p>
              {item.sessions?.[0] && (
                <p className="mt-4 flex gap-2 text-sm text-gray-400">
                  <Calendar className="h-4 w-4" />
                  {new Date(item.sessions[0].starts_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                </p>
              )}
              <Link to={`/live/${item.program.type}/${item.program.slug}`} className="mt-5 flex items-center justify-center gap-2 rounded bg-zinc-800 py-3 text-amber-300 hover:bg-zinc-700">
                <Video className="h-4 w-4" />Details and joining window
              </Link>
            </article>
          ))}
        </div>
      ) : <Empty icon={Video} text="You haven't registered for a live program yet" href="/live/webinars" link="Browse Live Programs" />}
    </section>
  );
}

function CertificateSection({ certificates, downloadError, onDownload }) {
  return (
    <section className="mt-14">
      <h2 className="text-2xl font-bold">My Certificates</h2>
      {downloadError && <p role="alert" className="mt-4 text-red-300">{downloadError}</p>}
      {certificates.length ? (
        <div className="mt-5 grid gap-5 md:grid-cols-2">
          {certificates.map((certificate) => (
            <article key={certificate.id} className="rounded-xl border border-zinc-800 bg-zinc-900 p-6">
              <Award className="h-9 w-9 text-yellow-400" />
              <h3 className="mt-3 text-lg font-semibold">{certificate.course}</h3>
              <p className="mt-1 text-sm text-gray-400">
                {certificate.number} - <span className="capitalize">{certificate.status}</span>
              </p>
              {certificate.issued_at && <p className="mt-1 text-xs text-gray-500">Issued {new Date(certificate.issued_at).toLocaleDateString('en-IN')}</p>}
              {certificate.downloadable && (
                <button onClick={() => onDownload(certificate)} className="mt-4 flex items-center gap-2 rounded bg-yellow-400 px-4 py-2 font-medium text-black">
                  <Download className="h-4 w-4" />Download PDF
                </button>
              )}
            </article>
          ))}
        </div>
      ) : <Empty icon={Award} text="Complete all configured course requirements to earn a certificate" href="/courses" link="Continue Learning" />}
    </section>
  );
}

function Empty({ icon: Icon, text, href, link }) {
  return (
    <div className="mt-5 rounded-xl border border-zinc-800 bg-zinc-900 py-16 text-center">
      <Icon className="mx-auto h-14 w-14 text-gray-600" />
      <p className="mt-4 text-gray-400">{text}</p>
      <Link to={href} className="mt-5 inline-block rounded bg-yellow-500 px-5 py-3 font-medium text-black">{link}</Link>
    </div>
  );
}
